import React, { ChangeEvent } from 'react';
import Table from '../../../../../components/Table/Table';
import { IClaimDetailItem, INewClaimLine } from '../../../../../types';
import { Button, TextArea } from '@orderly/morrisons-component-library';
import { toastr } from 'react-redux-toastr';
import styles from './ClaimDetailsTable.module.scss';

interface IClaimDetailsProps {
  canClaim: boolean;
  items: IClaimDetailItem[];
  newLines: { [key: string]: INewClaimLine[] };
  setNewLines: React.Dispatch<React.SetStateAction<{ [key: string]: INewClaimLine[] }>>;
}

enum ClaimReasons {
  ShortDelivered = 'Short Delivered',
  OverDelivered = 'Over Delivered',
  TemperatureRejection = 'Temperature Rejection',
  Damaged = 'Damaged',
  ShortLife = 'Short Shelf Life',
  TobaccoShortDelivered = 'Tobacco Short Delivery',
  TobaccoOverDelivered = 'Tobacco Over Delivered',
}

const ClaimDetailsTable: React.FC<IClaimDetailsProps> = ({
  items,
  newLines,
  setNewLines,
  canClaim,
}) => {
  const excludedReasons = [ClaimReasons.OverDelivered, ClaimReasons.TobaccoOverDelivered];
  const columns = [
    { accessorKey: 'description', header: 'Product Name', className: styles.name },
    { accessorKey: 'skuPin', header: 'Product ID', className: '' },
    { accessorKey: 'unitsOrdered', header: 'Ordered', className: '' },
    { accessorKey: 'unitsDelivered', header: 'Delivered', className: '' },
    { accessorKey: 'productsAffected', header: 'Affected', className: '' },
    { accessorKey: 'reasonCode', header: 'Reason', className: '' },
    { accessorKey: 'comment', header: 'Comments', className: '' },
    canClaim && { accessorKey: 'actions', header: 'Actions', className: '' },
  ];

  const handleImageUpload = (skuPin: string, index: number, e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const currentLines = [...(newLines[skuPin] || [])];
    const existingAttachments = currentLines[index].attachments || [];

    if (existingAttachments.length + selectedFiles.length > 4) {
      toastr.warning('Upload Limit Exceeded', 'You can only upload up to 4 images.');
      return;
    }

    const readerPromises = selectedFiles.map((file) => {
      return new Promise<{ fileName: string; contentType: string; content: string }>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            const base64String = (reader.result as string).split(',')[1];
            resolve({
              fileName: file.name,
              contentType: file.type,
              content: base64String,
            });
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readerPromises)
      .then((fileDataArray) => {
        const updatedLines = [...(newLines[skuPin] || [])];
        updatedLines[index] = {
          ...updatedLines[index],
          attachments: [...updatedLines[index].attachments, ...fileDataArray],
        };

        setNewLines({
          ...newLines,
          [skuPin]: updatedLines,
        });

        toastr.success(
          'Attachments Added',
          `${fileDataArray.length} Attachment${fileDataArray.length === 1 ? ' has' : 's have'}
          ${' '}been added to the claim`,
        );
      })
      .catch(error => console.error('Error reading files:', error));
  };

  const handleAddNewLine = (skuPin: string) => {
    const updatedLines = [...(newLines[skuPin] || [])];
    const usedReasons = updatedLines.map(line => line.reasonCode);
    const availableReason = Object.values(ClaimReasons)
      .find(reason => !usedReasons.includes(reason));

    if (!availableReason) return;

    const newLine: INewClaimLine = {
      reasonCode: availableReason,
      comment: '',
      skuPin: parseInt(skuPin, 10),
      productsAffected: 0,
      attachments: [],
    };

    setNewLines({
      ...newLines,
      [skuPin]: [
        ...updatedLines,
        newLine,
      ],
    });
  };

  const handleProductsAffectedChange = (
    skuPin: string,
    index: number,
    value: number,
    maxQuantity: number,
    reasonCode: string) => {
    let newValue = value;
    if (isNaN(newValue)) {
      newValue = 0;
    }

    if (!excludedReasons.includes(reasonCode as ClaimReasons) && newValue > maxQuantity) {
      newValue = maxQuantity;
    }

    if (newValue < 0) {
      newValue = 0;
    }

    const updatedLines = [...(newLines[skuPin] || [])];
    updatedLines[index] = {
      ...updatedLines[index],
      productsAffected: newValue,
    };

    setNewLines({
      ...newLines,
      [skuPin]: updatedLines,
    });
  };

  const handleDeleteAttachments = (
    skuPin: string,
    index: number) => {

    const updatedLines = [...(newLines[skuPin] || [])];
    const attachmentCount = updatedLines[index].attachments.length;

    updatedLines[index] = {
      ...updatedLines[index],
      attachments: [],
    };

    setNewLines({
      ...newLines,
      [skuPin]: updatedLines,
    });

    toastr.success(
      `Attachment${attachmentCount === 1 ? '' : ''} removed`,
      `All attachment${attachmentCount === 1 ? '' : ''} have been removed`,
    );
  };

  const handleReasonCodeChange = (skuPin: string, index: number, value: string) => {
    const updatedLines = [...(newLines[skuPin] || [])];
    const usedReasons = new Set(updatedLines.map((line, i) => i !== index && line.reasonCode));
    if (usedReasons.has(value)) return;

    updatedLines[index] = {
      ...updatedLines[index],
      reasonCode: value,
    };

    const line = updatedLines[index];
    const maxQuantity = calculateRemainingQuantity(skuPin, line.productsAffected, updatedLines);

    if (!excludedReasons.includes(value as ClaimReasons) &&
      line.productsAffected > maxQuantity) {
      updatedLines[index] = {
        ...updatedLines[index],
        reasonCode: value,
        productsAffected: maxQuantity,
      };
    }

    setNewLines({
      ...newLines,
      [skuPin]: updatedLines,
    });
  };

  const handleCommentChange = (skuPin: string, index: number, comment: string) => {
    const trimmedComment = comment.slice(0, 2000);

    const updatedLines = [...(newLines[skuPin] || [])];
    updatedLines[index].comment = trimmedComment;

    setNewLines({
      ...newLines,
      [skuPin]: updatedLines,
    });
  };

  const calculateRemainingQuantity = (
    skuPin: string,
    productsAffected: number,
    updatedLines?: INewClaimLine[],
  ): number => {
    const newLine = updatedLines ? updatedLines : (newLines[skuPin] || []);
    let totalProductsAffected = newLine
      .filter(x => !excludedReasons.includes(x.reasonCode as ClaimReasons))
      .reduce((sum, line) => sum + line.productsAffected, 0);

    totalProductsAffected = totalProductsAffected - productsAffected;

    if (totalProductsAffected < 0) {
      totalProductsAffected = 0;
    }
    const item = items.find(item => item.skuPin === skuPin);
    const itemClaimableQuantity = item ? item.claimableQuantity : 0;
    return itemClaimableQuantity - totalProductsAffected;
  };

  const handleDeleteLine = (skuPin: string, index: number) => {
    const updatedLines = [...(newLines[skuPin] || [])];
    updatedLines.splice(index, 1);
    setNewLines({
      ...newLines,
      [skuPin]: updatedLines,
    });
  };

  return (
    <Table className="">
      <thead>
        <tr>
          {columns.map(column => (
            <th
              className={column.className}
              key={column.accessorKey}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map(item => (
          <React.Fragment key={item.skuPin}>
            <tr>
              <td className={styles.name}>{item.description}</td>
              <td>{item.skuPin}</td>
              <td>{item.unitsOrdered}</td>
              <td>{item.unitsDelivered}</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              {canClaim &&
                <td className={styles.actions}>
                  <span title="Add Line">
                    <Button
                      className={styles.button}
                      icon="icon-plus"
                      type="button"
                      text=""
                      onClick={() => handleAddNewLine(item.skuPin)}
                    />
                  </span>
                </td>
              }
            </tr>
            {item.lines.map(line => (
              <tr key={line.id}>
                <td />
                <td colSpan={3} />
                <td>{line.productsAffected}</td>
                <td>{line.reasonCode}</td>
                <td className={styles.commentCell}>{line.comment}</td>
                {canClaim && <td />}
              </tr>
            ))}

            {(newLines[item.skuPin] || []).map((line, index) => {
              const remainingQuantity = calculateRemainingQuantity(
                item.skuPin,
                line.productsAffected,
              );

              return (
                <tr key={`new-line-${item.skuPin}-${index}`}>
                  <td />
                  <td colSpan={3} />
                  <td>
                    <input
                      className={styles.productsAffected}
                      type="number"
                      value={line.productsAffected}
                      onChange={e => handleProductsAffectedChange(
                        item.skuPin,
                        index,
                        parseInt(e.target.value, 10),
                        remainingQuantity,
                        line.reasonCode,
                      )}
                      max={!excludedReasons.includes(line.reasonCode as ClaimReasons) ? remainingQuantity : undefined}
                    />
                  </td>
                  <td>
                    <select
                      className={styles.reasonCode}
                      value={line.reasonCode}
                      onChange={e => handleReasonCodeChange(item.skuPin, index, e.target.value as ClaimReasons)}
                    >
                      {Object.values(ClaimReasons)
                        .map(reason => (
                          <option
                            key={reason}
                            value={reason}
                            disabled={(newLines[item.skuPin] || [])
                              .some((l, i) => i !== index && l.reasonCode === reason)}
                          >
                            {reason}
                          </option>
                        ))}
                    </select>
                  </td>
                  <td>
                    <TextArea
                      name={`textarea-${item.skuPin}-${index}`}
                      label=""
                      className={styles.comment}
                      value={line.comment}
                      onChange={e => handleCommentChange(item.skuPin, index, e)}
                    />
                  </td>
                  <td className={styles.actions}>
                    {line.attachments.length > 0 ?
                      <span title="Delete Attachments">
                        <Button
                          className={styles.button}
                          icon="icon-Delete"
                          text=""
                          type="button"
                          onClick={() => handleDeleteAttachments(item.skuPin, index)}
                        />
                      </span> :
                      <span title="Upload Images">
                        <Button
                          className={styles.button}
                          icon={'icon-cloud-upload'}
                          text=""
                          type="button"
                          onClick={() => document.getElementById(`file-input-${item.skuPin}-${index}`).click()}
                        />
                        <input
                          id={`file-input-${item.skuPin}-${index}`}
                          type="file"
                          accept="image/png, image/jpeg"
                          multiple
                          style={{ display: 'none' }}
                          onChange={e => handleImageUpload(item.skuPin, index, e)}
                        />
                      </span>
                    }
                    <span title="Delete Line">
                      <Button
                        className={styles.button}
                        icon={`${styles.red} icon-Delete`}
                        text=""
                        type="button"
                        onClick={() => handleDeleteLine(item.skuPin, index)}
                      />
                    </span>
                  </td>
                </tr>
              );
            })}
          </React.Fragment>
        ))}
      </tbody>
    </Table>
  );
};

export default ClaimDetailsTable;
